import { useState } from 'react';

import { Badge, Divider, Label, Modal, Text, toastService } from '@thefarmersfront/kitchen';

import { ProgressCount, Table } from '~/components/common';

import { useModal } from '~/hooks/common';
import { useGetReceivingProgressItemIssueImagesMutation } from '~/hooks/receive';
import { useUserInfoStore } from '~/store';
import { formatReceiveExpectedDateTime, getReceiveStatusDetailModalColumnDefs } from '~/utils';

import { RECEIVE_STATUS_STATUS, RECEIVE_STATUS_STATUS_BADGE_COLOR, RECEIVE_TOOLTIP_MESSAGE } from '~/constants';

import { ProgressBar } from '../../common/ProgressBar';
import ReceiveStatusIssueImageModal from './ReceiveStatusIssueImageModal/ReceiveStatusIssueImageModal';

import type { TypeReceiveStatusDetailIssueImage, TypeReceiveStatusDetailItemInfo } from '~/types/receive.types';

import styles from './ReceiveStatusDetailModal.module.scss';

type TypeReceiveStatusDetailModalProps = {
  open: boolean;
  data: TypeReceiveStatusDetailItemInfo & { receiveRequestId: number };
  onConfirm: () => void;
};

/**
 * [입고관리 > 입고현황] 입고 현황 상세 모달
 */
const ReceiveStatusDetailModal = ({ open, data, onConfirm }: TypeReceiveStatusDetailModalProps) => {
  const { summary, items, progressCount, receiveRequestId } = data;

  const issueImagesMutation = useGetReceivingProgressItemIssueImagesMutation();

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const [issueImageList, setIssueImageList] = useState<TypeReceiveStatusDetailIssueImage[] | null>(null);

  const issueImageModal = useModal({
    onOpen: (requestItemId?: number) => {
      if (!requestItemId) return;

      issueImagesMutation.mutate(
        { ownerCode, requestId: receiveRequestId, requestItemId },
        {
          onSuccess: ({ data }) => {
            if (data.issueImages.length === 0) {
              toastService.addToast({ type: 'error', message: '이슈 이미지가 없습니다.' });
              return;
            }
            setIssueImageList(data.issueImages);
          },
        }
      );
    },
    onClose: () => setIssueImageList(null),
  });

  const { columnDefs, gridOptions } = getReceiveStatusDetailModalColumnDefs({
    hasPermissionNFA,
    onClickViewIssueImage: issueImageModal.handleOpenModal,
  });

  return (
    <>
      <Modal
        title={
          <div className={styles['modal-title-wrapper']}>
            <Text className={styles['modal-title']} size="xxlarge" weight="semi-bold">
              입고 현황 상세
            </Text>
            <Badge variant="light" status color={RECEIVE_STATUS_STATUS_BADGE_COLOR[summary?.status]}>
              {RECEIVE_STATUS_STATUS[summary?.status]}
            </Badge>
          </div>
        }
        size="xlarge"
        open={open}
        onConfirm={onConfirm}
      >
        <div className={styles['modal-content']}>
          <div className={styles['content-row']}>
            <div className={styles['content-section']}>
              <Text className={styles['section-title']} size="xxlarge" weight="bold">
                요청 정보
              </Text>
              <div className={styles['request-info']}>
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="입고요청번호" />
                  <Text size="large">{summary?.requestCode}</Text>
                </div>
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="요청등록일" />
                  <Text size="large">{summary?.requestDate ?? '-'}</Text>
                </div>
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="입고예정일시" />
                  <Text size="large">
                    {formatReceiveExpectedDateTime(summary?.estimatedArrivalDate, summary?.estimatedArrivalTime)}
                  </Text>
                </div>
                {summary?.xdock === '이용함' && (
                  <div className={styles['input-row']}>
                    <Label
                      className={styles['label']}
                      title="최종입고예정일"
                      tooltip={{ description: RECEIVE_TOOLTIP_MESSAGE.FINAL_RECEIVE_EXPECTED_DATE }}
                    />
                    <Text size="large">{summary?.finalEstimatedArrivalDate ?? '-'}</Text>
                  </div>
                )}
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="입고 센터" />
                  <Text size="large">
                    {summary?.receivingClusterName} / {summary?.receivingWarehouseName}
                  </Text>
                </div>
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="공급사" />
                  <Text className={styles['supplier-text']} size="large">
                    {summary?.supplierName}
                    <Text size="large" color="quaternary">
                      {summary?.supplierCode}
                    </Text>
                  </Text>
                </div>
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="입고 방법" />
                  <Text size="large">{summary?.releaseCodeName}</Text>
                </div>
                <div className={styles['input-row']}>
                  <Label className={styles['label']} title="입고대행서비스" />
                  <Text size="large">{summary?.xdock}</Text>
                </div>
              </div>
            </div>
            <div className={styles['content-section']}>
              <Text className={styles['section-title']} size="xxlarge" weight="bold">
                처리 현황
              </Text>
              <div className={styles['status-info']}>
                <div className={styles['info-row']}>
                  <Text className={styles['info-title']} size="large" weight="semi-bold">
                    수량
                  </Text>
                  <ProgressBar
                    percent={[
                      {
                        value: ((progressCount?.passedUnit ?? 0) / (progressCount?.totalUnit ?? 1)) * 100,
                        color: 'success',
                      },
                      {
                        value: ((progressCount?.issuedUnit ?? 0) / (progressCount?.totalUnit ?? 1)) * 100,
                        color: 'error',
                      },
                    ]}
                  />
                  <div className={styles['info-value']}>
                    <ProgressCount
                      className={styles['progress-count']}
                      title="전체"
                      value={progressCount?.totalUnit}
                      showDashIfZero
                    />
                    <ProgressCount
                      className={styles['progress-count']}
                      title="정상"
                      value={progressCount?.passedUnit}
                      valueColor="success"
                      showDashIfZero
                    />
                    <ProgressCount
                      className={styles['progress-count']}
                      title="문제"
                      value={progressCount?.issuedUnit}
                      valueColor="error"
                      showDashIfZero
                    />
                  </div>
                </div>
                <Divider />
                <div className={styles['info-row']}>
                  <Text className={styles['info-title']} size="large" weight="semi-bold">
                    품목
                  </Text>
                  <div className={styles['info-value']}>
                    <ProgressCount
                      className={styles['progress-count']}
                      title="전체"
                      value={progressCount?.totalSku}
                      showDashIfZero
                    />
                    <ProgressCount
                      className={styles['progress-count']}
                      title="정상"
                      value={progressCount?.passedSku}
                      valueColor="success"
                      showDashIfZero
                    />
                    <ProgressCount
                      className={styles['progress-count']}
                      title="문제"
                      value={progressCount?.issuedSku}
                      valueColor="error"
                      showDashIfZero
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles['content-section']}>
            <Text className={styles['section-title']} size="xxlarge" weight="bold">
              입고 처리 상세
            </Text>
            <div className={styles['table-wrapper']}>
              <Table className={styles['table']} data={items ?? []} options={{ columnDefs, ...gridOptions }} />
            </div>
          </div>
        </div>
      </Modal>
      {issueImageModal.isOpen && issueImageList && (
        <ReceiveStatusIssueImageModal
          imageList={issueImageList}
          isOpen={issueImageModal.isOpen}
          onClose={issueImageModal.handleCloseModal}
        />
      )}
    </>
  );
};

export default ReceiveStatusDetailModal;
